import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TabSharingStatus,
  WACallStates,
  WACallStatus,
  WAStorageState,
  WA_STORAGE_KEY
} from '../pages/CaseDetails/feedbackForm/component/CallBridge/WhatsappCalls/interface';

export interface WhatsappCallReduxState {
  status: WACallStatus;
  error: string;
  isCallCompleted: boolean;
  pendingPhone: string;
  ownerTabId: string;
  callLoanId: string;
  callCustomerId: string;
  lastUpdated: number;
  tabSharingStatus: TabSharingStatus;
  recordingUrl: string | null;
  recordingMimeType: string | null;
  feedbackInteractionReferenceId: string | null;
}

const INITIAL_STATE: WhatsappCallReduxState = {
  status: WACallStates.IDLE,
  error: '',
  isCallCompleted: false,
  pendingPhone: '',
  ownerTabId: '',
  callLoanId: '',
  callCustomerId: '',
  lastUpdated: 0,
  tabSharingStatus: 'none',
  recordingUrl: null,
  recordingMimeType: null,
  feedbackInteractionReferenceId: null
};

// Initial hydration from localStorage so a page reload restores the in-progress call view.
const hydrateFromStorage = (): WhatsappCallReduxState => {
  try {
    const raw = localStorage.getItem(WA_STORAGE_KEY);
    if (!raw) return INITIAL_STATE;
    const stored: WAStorageState = JSON.parse(raw);
    return {
      status: stored.status || WACallStates.IDLE,
      error: stored.error || '',
      isCallCompleted: stored.isCallCompleted || false,
      pendingPhone: stored.pendingPhone || '',
      ownerTabId: stored.ownerTabId || '',
      callLoanId: stored.loanId || '',
      callCustomerId: stored.customerId || '',
      lastUpdated: stored.lastUpdated || 0,
      tabSharingStatus: stored.tabSharingStatus || 'none',
      recordingUrl: null,
      recordingMimeType: null,
      feedbackInteractionReferenceId: null
    };
  } catch {
    return INITIAL_STATE;
  }
};

interface CallRequestedPayload {
  phone: string;
  loanId: string;
  customerId: string;
  ownerTabId: string;
}

const whatsappCallSlice = createSlice({
  name: 'whatsappCall',
  initialState: hydrateFromStorage(),
  reducers: {
    callRequested(state, action: PayloadAction<CallRequestedPayload>) {
      state.status = WACallStates.CHECKING_LOGIN;
      state.error = '';
      state.isCallCompleted = false;
      state.recordingUrl = null;
      state.recordingMimeType = null;
      state.pendingPhone = action.payload.phone;
      state.callLoanId = action.payload.loanId;
      state.callCustomerId = action.payload.customerId;
      state.ownerTabId = action.payload.ownerTabId;
      state.tabSharingStatus = 'none';
      state.lastUpdated = Date.now();
    },
    setStep(state, action: PayloadAction<WACallStatus>) {
      state.status = action.payload;
      state.lastUpdated = Date.now();
    },
    setError(state, action: PayloadAction<string>) {
      state.status = WACallStates.ERROR;
      state.error = action.payload;
      state.lastUpdated = Date.now();
    },
    tabShareGranted(state) {
      state.tabSharingStatus = 'active';
      state.lastUpdated = Date.now();
    },
    setRecordingUrl(state, action: PayloadAction<{ url: string | null; mimeType: string | null }>) {
      state.recordingUrl = action.payload.url;
      state.recordingMimeType = action.payload.mimeType;
    },
    callCompleted(state) {
      state.isCallCompleted = true;
      state.status = WACallStates.UPLOADING;
      state.lastUpdated = Date.now();
    },
    uploadComplete(state) {
      state.isCallCompleted = false;
      state.status = WACallStates.AWAITING_FEEDBACK;
      state.tabSharingStatus = 'none';
      state.lastUpdated = Date.now();
    },
    setFeedbackInteractionReferenceId(state, action: PayloadAction<string>) {
      state.feedbackInteractionReferenceId = action.payload;
    },
    clearFeedbackInteractionReferenceId(state) {
      state.feedbackInteractionReferenceId = null;
    },
    feedbackSubmitted(state) {
      state.isCallCompleted = false;
      state.status = WACallStates.IDLE;
      state.error = '';
      state.recordingUrl = null;
      state.recordingMimeType = null;
      state.pendingPhone = '';
      state.callLoanId = '';
      state.callCustomerId = '';
      state.tabSharingStatus = 'none';
      state.feedbackInteractionReferenceId = null;
      state.lastUpdated = Date.now();
    },
    resetCall() {
      return { ...INITIAL_STATE };
    },
    syncFromStorage(_state, action: PayloadAction<WAStorageState>) {
      const storedState = action.payload;
      return {
        status: storedState.status || WACallStates.IDLE,
        error: storedState.error || '',
        isCallCompleted: storedState.isCallCompleted || false,
        pendingPhone: storedState.pendingPhone || '',
        ownerTabId: storedState.ownerTabId || '',
        callLoanId: storedState.loanId || '',
        callCustomerId: storedState.customerId || '',
        lastUpdated: storedState.lastUpdated || 0,
        tabSharingStatus: storedState.tabSharingStatus || 'none',
        recordingUrl: null,
        recordingMimeType: null,
        feedbackInteractionReferenceId: null
      };
    }
  }
});

export const {
  callRequested,
  setStep,
  setError,
  tabShareGranted,
  setRecordingUrl,
  callCompleted,
  uploadComplete,
  setFeedbackInteractionReferenceId,
  clearFeedbackInteractionReferenceId,
  feedbackSubmitted,
  resetCall,
  syncFromStorage
} = whatsappCallSlice.actions;

export default whatsappCallSlice.reducer;
